import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Paper,
  Typography,
  Grid
} from '@mui/material'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import PieChartIcon from '@mui/icons-material/PieChart'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import moment from 'moment-timezone'
import { colors, dateFormats } from '../../consts'
import { ChartDataTable } from '../atoms'
import styles from './QASummaryCharts.module.scss'

const criteriaLabels = {
  callerNameConfirmation: 'Caller Name',
  companyNameConfirmation: 'Company Name',
  emailPhonetics: 'Email Verification',
  phoneNumberVerification: 'Phone Number',
  opposingPartyPhonetics: 'Opposing Party',
  callNotes5Ws: 'Call Notes (5Ws)'
}

const PIE_COLORS = [
  '#ef4444',
  '#f59e0b',
  '#eab308',
  '#22c55e',
  '#06b6d4',
  '#8b5cf6'
]

const TrendTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const formattedDate = (typeof label === 'string' && label.includes('-'))
      ? moment.tz(label, 'YYYY-MM-DD', dateFormats.timezone).format('MMM D, YYYY')
      : label

    return (
      <Paper className={styles.tooltipPaper}>
        <Typography className={styles.tooltipTitle}>
          {formattedDate}
        </Typography>
        {payload.map((entry, index) => (
          <Typography key={index} className={styles.tooltipEntry} style={{ color: entry.color }}>
            {entry.name}: {entry.value.toFixed(1)}%
          </Typography>
        ))}
      </Paper>
    )
  }
  return null
}

TrendTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string
}

const PieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0]
    return (
      <Paper className={styles.tooltipPaper}>
        <Typography className={styles.tooltipName}>
          {data.name}
        </Typography>
        <Typography className={styles.tooltipSubtext}>
          {data.value} failures ({data.payload.percentage.toFixed(1)}%)
        </Typography>
      </Paper>
    )
  }
  return null
}

PieTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array
}

const QASummaryCharts = ({ chartData = { trend: [], criteriaDistribution: [] }, statistics = { byCriteria: [] }, loading = false }) => {
  const { trend = [] } = chartData || {}
  const { byCriteria = [] } = statistics || {}

  const pieData = byCriteria
    .filter(c => c.failCount > 0)
    .map(c => {
      const total = (c.failCount || 0) + (c.passCount || 0)
      return {
        name: criteriaLabels[c.key] || c.label || c.name || c.key,
        value: c.failCount,
        percentage: total > 0 ? (c.failCount / total) * 100 : 0
      }
    })
    .sort((a, b) => b.value - a.value)

  const trendData = (trend || []).map(item => ({
    date: item.date,
    passRate: parseFloat(item.passRate) || 0,
    total: item.total || 0
  }))

  const hasTrendData = trendData.length > 0
  const hasPieData = pieData.length > 0

  if (loading) {
    return (
      <Box className={styles.root}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Paper className={styles.chartPaper}>
              <Box className={styles.chartHeader}>
                <TrendingUpIcon className={styles.chartIcon} />
                <Typography className={styles.chartTitle}>Pass Rate Trend</Typography>
              </Box>
              <Skeleton height={300} />
            </Paper>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Paper className={styles.chartPaper}>
              <Box className={styles.chartHeader}>
                <PieChartIcon className={styles.chartIcon} />
                <Typography className={styles.chartTitle}>Failure Distribution</Typography>
              </Box>
              <Skeleton height={300} />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    )
  }

  return (
    <Box className={styles.root}>
      <Grid container spacing={3}>
        {/* Pass Rate Trend Line Chart */}
        <Grid item xs={12} lg={8}>
          <Paper className={styles.chartPaper}>
            <Box className={styles.chartHeader}>
              <TrendingUpIcon className={styles.chartIcon} />
              <Typography className={styles.chartTitle}>Pass Rate Trend</Typography>
            </Box>
            {hasTrendData ? (
              <>
              <Box className={styles.chartContainer} role='img' aria-label='QA pass rate trend over time line chart'>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={trendData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }}
                      axisLine={{ stroke: 'var(--color-border)' }}
                      tickFormatter={(value) =>
                        moment.tz(value, 'YYYY-MM-DD', dateFormats.timezone).format('MM/DD')
                      }
                    />
                    <YAxis
                      domain={[0, 100]}
                      tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }}
                      axisLine={{ stroke: 'var(--color-border)' }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <RechartsTooltip content={<TrendTooltip />} />
                    <Legend />
                    <defs>
                      <linearGradient id="passRateGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="5%" stopColor={colors.brand.tertiary} stopOpacity={0.9} />
                        <stop offset="95%" stopColor={colors.brand.primary} stopOpacity={0.9} />
                      </linearGradient>
                    </defs>
                    <Line
                      type="monotone"
                      dataKey="passRate"
                      stroke="url(#passRateGradient)"
                      strokeWidth={3}
                      name="Pass Rate"
                      dot={{
                        r: 4,
                        strokeWidth: 2,
                        stroke: '#fff',
                        fill: colors.brand.primary
                      }}
                      activeDot={{
                        r: 6,
                        strokeWidth: 2,
                        stroke: '#fff',
                        fill: colors.brand.tertiary
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
              <ChartDataTable
                data={trendData}
                columns={[
                  {
                    key: 'date',
                    label: 'Date',
                    format: (v) => moment.tz(v, 'YYYY-MM-DD', dateFormats.timezone).format('MMM D, YYYY')
                  },
                  { key: 'passRate', label: 'Pass Rate (%)', format: (v) => `${v.toFixed(1)}%` },
                  { key: 'total', label: 'Total Evaluations' }
                ]}
                caption='QA Pass Rate Trend'
              />
              </>
            ) : (
              <Box className={styles.emptyState}>
                No trend data available for the selected filters
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Failure Distribution Pie Chart */}
        <Grid item xs={12} lg={4}>
          <Paper className={styles.chartPaper}>
            <Box className={styles.chartHeader}>
              <PieChartIcon className={styles.chartIcon} />
              <Typography className={styles.chartTitle}>Failure Distribution</Typography>
            </Box>
            {hasPieData ? (
              <>
                <Box className={styles.chartContainer} role='img' aria-label='QA failure distribution by criteria pie chart'>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={PIE_COLORS[index % PIE_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<PieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Box className={styles.legendContainer}>
                  {pieData.map((entry, index) => (
                    <Box key={entry.name} className={styles.legendItem}>
                      <Box
                        className={styles.legendDot}
                        style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                      />
                      <span>{entry.name}</span>
                    </Box>
                  ))}
                </Box>
                <ChartDataTable
                  data={pieData}
                  columns={[
                    { key: 'name', label: 'Criteria' },
                    { key: 'value', label: 'Failures' },
                    { key: 'percentage', label: 'Failure Rate', format: (v) => `${v.toFixed(1)}%` }
                  ]}
                  caption='QA Failure Distribution by Criteria'
                />
              </>
            ) : (
              <Box className={styles.emptyState}>
                No failure data available
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

QASummaryCharts.propTypes = {
  chartData: PropTypes.shape({
    trend: PropTypes.arrayOf(PropTypes.shape({
      date: PropTypes.string,
      passRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      total: PropTypes.number
    })),
    criteriaDistribution: PropTypes.array
  }),
  statistics: PropTypes.shape({
    byCriteria: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
      failCount: PropTypes.number,
      total: PropTypes.number
    }))
  }),
  loading: PropTypes.bool
}

export default QASummaryCharts
